import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import { round } from "lodash";
import styles from "./index.module.scss";
import { getBacket } from "../../../../redux/action-creaters/cart";
import { showPopup } from "/redux/action-creaters/popup";
import { popupTypes } from "/redux/reducers/popupReducer";
import { Api } from "/api";
import Button from "../../../common/button";
import Icon from "../../../common/icon";
import { formatPrice, isMoreThanOneYearAgo } from "../../../../utils";
import {
  CART_GOALS,
  GOALS,
  reachCartGoal,
  reachGoal,
} from "../../../../utils/tracking";

export default function Offer({ product, isDetail = false }) {
  const name = product.NAME;
  const productId = product.ID;
  const catalog = product.CATALOG;

  const { sections } = useSelector((state) => state.sections);

  const dispatch = useDispatch();
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const { basket } = useSelector((state) => state.cart);

  const isInCart = useMemo(() => {
    return basket?.ITEMS?.find((item) => +productId === +item.PRODUCT_ID);
  }, [basket]);

  const quantity = useMemo(() => {
    return catalog.QUANTITY;
  }, [catalog]);

  const isAlailable = useMemo(() => {
    return catalog.AVAILABLE;
  }, [catalog]);

  const price = useMemo(() => {
    return catalog.MIN_PRICE;
  }, [catalog]);

  const noDiscoutPrice = useMemo(() => {
    return catalog.PRICE;
  }, [catalog]);

  const edostMinPriceCorrected = useMemo(() => {
    return 0;
  }, [catalog]);

  const priceWithCity = useMemo(() => {
    return price + edostMinPriceCorrected;
  }, [edostMinPriceCorrected]);
  // eslint-disable-next-line no-unused-vars
  const { user } = useSelector((state) => state.user);

  const { cityName } = useSelector((state) => state.location);

  // eslint-disable-next-line no-unused-vars
  const installPrice = useMemo(() => {
    if (!sections[product.IBLOCK_SECTION_ID]?.UF_INSTALL_PRICE) {
      return 0;
    }
    const cityList = {
      Москва: "msk",
      "Санкт-Петербург": "spb",
    };
    const cityCode = cityList[cityName];
    if (!cityCode) {
      return 0;
    }

    const sectionHourPrices = {};
    sections[product.IBLOCK_SECTION_ID].UF_INSTALL_PRICE.forEach((item) => {
      const itemArray = item.split(":");
      sectionHourPrices[itemArray[0]] = +itemArray[1];
    });
    const installPriceForCity = sectionHourPrices[cityCode];
    if (installPriceForCity && product.VREMYA_USTANOVKI) {
      let price = round((installPriceForCity / 60) * product.VREMYA_USTANOVKI);
      if (product.INSTALL_COEFFICIENT) {
        price = round((price * product.INSTALL_COEFFICIENT) / 100);
      }
      return price;
    }
    return 0;
  }, [product, catalog, cityName]);
  // eslint-disable-next-line no-unused-vars
  const { contactsData } = useSelector((state) => state.contacts);
  const router = useRouter();

  const blockRef = useRef(null);
  const [isShowMobileBuy, setIsShowMobileBuy] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!blockRef.current) return;
      if (window.innerWidth > 991) return;

      const rect = blockRef.current.getBoundingClientRect();
      const offsetTop = rect.top + window.scrollY - 150;
      const { scrollY } = window;

      if (scrollY > offsetTop + blockRef.current.offsetHeight) {
        setIsShowMobileBuy(true);
      } else {
        setIsShowMobileBuy(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // проверка при загрузке

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const buy = async () => {
    if (!isInCart) {
      setIsAddingToCart(true);
      await Api.basket.addToBasket(productId, priceWithCity);
      await dispatch(getBacket());
      await dispatch(showPopup(popupTypes.cart));
      setIsAddingToCart(false);

      reachGoal(GOALS.addToCart, user);
      reachCartGoal(CART_GOALS.addToCart, user);
    } else {
      router.push("/personal/cart/");
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      ecommerce: {
        currencyCode: "RUB",
        add: {
          products: [
            {
              id: productId,
              name,
              price,
              quantity: 1,
              category: sections[product.IBLOCK_SECTION_ID].NAME,
              brand: product.BRAND?.name,
              list: isDetail ? "Детальная страница" : "Каталог",
            },
          ],
        },
      },
    });
  };

  return (
    <>
      <div
        className={styles.Top}
        itemProp="offers"
        itemScope
        itemType="http://schema.org/Offer"
      >
        <div className={styles.Price}>
          {isAlailable && noDiscoutPrice > price && (
            <div className={styles.BeforeValue}>{noDiscoutPrice} руб</div>
          )}
          {!isAlailable && (
            <div className={styles.LastValue}>Последная цена:</div>
          )}
          <div className={styles.Value}>
            <span>
              <span itemProp="price">{formatPrice(priceWithCity)}</span> руб
            </span>
            {isAlailable && (
              <div className={styles.PriceInfo}>
                *стоимость товара без установки
              </div>
            )}
          </div>

          {isAlailable ? (
            <>
              <span itemProp="priceCurrency" style={{ display: "none" }}>
                RUB
              </span>
              <link itemProp="availability" href="http://schema.org/InStock" />
            </>
          ) : (
            <>
              <link
                itemProp="availability"
                href="http://schema.org/OutOfStock"
              />
            </>
          )}
        </div>

        <div className={styles.Qty}>
          {quantity > 0 ? (
            <>
              <Icon id="yes" width={18} height={18} />
              <span className={styles.InStock}>В наличии: {quantity} шт</span>
            </>
          ) : (
            <span className={styles.OutOfStock}>
              {" "}
              {isMoreThanOneYearAgo(product.TIMESTAMP_X)
                ? "Нет в наличии"
                : "Распродано"}
            </span>
          )}
        </div>
      </div>

      <div className={styles.Body} ref={blockRef}>
        {isAlailable ? (
          <>
            <div className={styles.Buttons}>
              <Button
                type={isInCart ? "outline" : ""}
                onClick={buy}
                label={isInCart ? "В корзине" : "Купить"}
                isLoading={isAddingToCart}
              />
              <Button
                type="outline"
                label="Купить в 1 клик"
                onClick={() => {
                  dispatch(
                    showPopup(popupTypes.buyOneClick, {
                      id: productId,
                      price: priceWithCity,
                    }),
                  );
                }}
              />
            </div>
            <span className={styles.Separator} />
            <div
              className={styles.Link}
              onClick={() => {
                dispatch(
                  showPopup(popupTypes.findLowPrice, {
                    name,
                    id: productId,
                    price: priceWithCity,
                  }),
                );
              }}
            >
              <div>
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 22 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.5 5.5H13C14.3807 5.5 15.5 6.61929 15.5 8C15.5 9.38071 14.3807 10.5 13 10.5H8.5V5.5ZM8.5 5.5V16.5M8.75 10.5H7M12 13.75H7M21 11C21 16.5228 16.5228 21 11 21C5.47715 21 1 16.5228 1 11C1 5.47715 5.47715 1 11 1C16.5228 1 21 5.47715 21 11Z"
                    stroke="#1247A5"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Нашли дешевле?</span>
              </div>

              <svg
                width="8"
                height="14"
                viewBox="0 0 8 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 13L7 7L1 1"
                  stroke="#9DA4AE"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </>
        ) : (
          <div className={styles.ButtonsEmpty}>
            <Button
              type="outline"
              label={"Подобрать аналог"}
              onClick={() => {
                dispatch(
                  showPopup(popupTypes.similarProduct, {
                    name: `${product.NAME} (арт ${product.ARTICLE?.VALUE}) (id ${product.ID})`,
                    id: productId,
                  }),
                );
              }}
            />

            {!isMoreThanOneYearAgo(product.TIMESTAMP_X) && (
              <div
                className={styles.Subscribe}
                onClick={() => {
                  dispatch(
                    showPopup(popupTypes.subscribeProduct, {
                      name: `${product.NAME} (арт ${product.ARTICLE?.VALUE}) (id ${product.ID})`,
                      id: productId,
                    }),
                  );
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                  <path d="M439.39 362.29c-19.32-20.76-55.47-51.99-55.47-154.29 0-77.7-54.48-139.9-127.94-155.16V32c0-17.67-14.32-32-31.98-32s-31.98 14.33-31.98 32v20.84C118.56 68.1 64.08 130.3 64.08 208c0 102.3-36.15 133.53-55.47 154.29-6 6.45-8.66 14.16-8.61 21.71.11 16.4 12.98 32 32.1 32h383.8c19.12 0 32-15.6 32.1-32 .05-7.55-2.61-15.27-8.61-21.71zM67.53 368c21.22-27.97 44.42-74.33 44.53-159.42 0-.2-.06-.38-.06-.58 0-61.86 50.14-112 112-112s112 50.14 112 112c0 .2-.06.38-.06.58.11 85.1 23.31 131.46 44.53 159.42H67.53zM224 512c35.32 0 63.97-28.65 63.97-64H160.03c0 35.35 28.65 64 63.97 64z" />
                </svg>
              </div>
            )}
          </div>
        )}
      </div>

      {isShowMobileBuy && (
        <div className={styles.BodyMobile}>
          <div className={styles.BodyMobilePrice}>
            <div className={styles.Qty}>
              {quantity > 0 ? (
                <span className={styles.InStock}>В наличии: {quantity} шт</span>
              ) : (
                <span className={styles.OutOfStock}>
                  {isMoreThanOneYearAgo(product.TIMESTAMP_X)
                    ? "Нет в наличии"
                    : "Распродано"}
                </span>
              )}
            </div>
            <div className={styles.Price}>
              <div className={styles.Value}>
                {isAlailable && (
                  <>
                    <span>
                      <span itemProp="price">{formatPrice(priceWithCity)}</span>{" "}
                      руб
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className={styles.BodyMobileButtons}>
            {!isAlailable ? (
              <div className={styles.ButtonsEmpty}>
                <Button
                  type="outline"
                  label={"Подобрать аналог"}
                  onClick={() => {
                    dispatch(
                      showPopup(popupTypes.similarProduct, {
                        name: `${product.NAME} (арт ${product.ARTICLE?.VALUE}) (id ${product.ID})`,
                        id: productId,
                      }),
                    );
                  }}
                />

                {!isMoreThanOneYearAgo(product.TIMESTAMP_X) && (
                  <div
                    className={styles.Subscribe}
                    onClick={() => {
                      dispatch(
                        showPopup(popupTypes.subscribeProduct, {
                          name: `${product.NAME} (арт ${product.ARTICLE?.VALUE}) (id ${product.ID})`,
                          id: productId,
                        }),
                      );
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 448 512"
                    >
                      <path d="M439.39 362.29c-19.32-20.76-55.47-51.99-55.47-154.29 0-77.7-54.48-139.9-127.94-155.16V32c0-17.67-14.32-32-31.98-32s-31.98 14.33-31.98 32v20.84C118.56 68.1 64.08 130.3 64.08 208c0 102.3-36.15 133.53-55.47 154.29-6 6.45-8.66 14.16-8.61 21.71.11 16.4 12.98 32 32.1 32h383.8c19.12 0 32-15.6 32.1-32 .05-7.55-2.61-15.27-8.61-21.71zM67.53 368c21.22-27.97 44.42-74.33 44.53-159.42 0-.2-.06-.38-.06-.58 0-61.86 50.14-112 112-112s112 50.14 112 112c0 .2-.06.38-.06.58.11 85.1 23.31 131.46 44.53 159.42H67.53zM224 512c35.32 0 63.97-28.65 63.97-64H160.03c0 35.35 28.65 64 63.97 64z" />
                    </svg>
                  </div>
                )}
              </div>
            ) : (
              <Button
                type={isInCart ? "outline" : ""}
                onClick={buy}
                label={isInCart ? "В корзине" : "Купить"}
                isLoading={isAddingToCart}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}
